import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// useScheduleSuggestions — drives ScheduleSuggestionsSection on the
// Schedule hub. Patterns are pulled from useHeroBrief.js (cache-first
// read, edge-fn lazy generate, synchronous inFlightRef double-fire
// guard) but with two intentional differences:
//
//   1. Cache MISS does NOT auto-generate. Cache miss → phase='idle' and
//      the user has to tap Generate. The hero brief is allowed to spend
//      on a page load; per-day suggestions are heavier and only worth
//      paying for on intent.
//   2. A 30-second client cooldown after a generate(). The edge fn has
//      no hard rate limit; the cooldown is the UI's debouncer against
//      pogo-clicking. cooldownRemainingMs ticks down each second so
//      the section can render "Wait Ns".
//
// Cache key: (restaurant_id, tab_key='schedule', sub_key='suggestions').
// 6h window matches the rest of ai_briefs.

const TAB_KEY = 'schedule'
const SUB_KEY = 'suggestions'
const FN_NAME = 'generate-schedule-suggestions'
const CACHE_WINDOW_HOURS = 6
const COOLDOWN_MS = 30_000

export function useScheduleSuggestions() {
  // Phases: idle | generating | ready | empty | error | cached
  // - 'idle'      → no cache row within the 6h window; waiting on user
  // - 'generating' → edge fn in flight
  // - 'cached'    → cache hit, payload populated
  // - 'ready'     → fresh generate() succeeded, payload populated
  // - 'empty'     → payload._empty (tiny-data nudge OR timeout retryable)
  // - 'error'     → edge fn returned a structured error
  const [phase, setPhase]               = useState('idle')
  const [payload, setPayload]           = useState(null)
  const [generatedAt, setGeneratedAt]   = useState(null)
  const [error, setError]               = useState(null)
  const [restaurantId, setRid]          = useState(null)
  const [cooldownRemainingMs, setCool]  = useState(0)

  // Multi-unit scope. null for single-unit → legacy first-active lookup
  // + no restaurant_id in the body (byte-identical). Non-null for a
  // grouped user → use the selected unit as the cache scope and send it
  // in the body so _shared/auth.ts re-targets (verified, never trusted).
  const { currentRestaurantId } = useCurrentUnit()

  // Synchronous double-fire guard (same pattern as useHeroBrief FIX H7).
  const inFlightRef = useRef(false)
  // Cooldown interval id so we can clear it on unmount.
  const cooldownTickRef = useRef(null)
  // Cooldown deadline timestamp so a re-render doesn't reset the clock.
  const cooldownEndsAtRef = useRef(0)

  // Resolve restaurant_id. Grouped: use the selected unit directly so
  // the cache scope + edge-fn target follow the UnitSwitcher. Single-unit
  // (null): legacy first-active lookup (matches useHeroBrief).
  useEffect(() => {
    if (currentRestaurantId) {
      setRid(currentRestaurantId)
      return
    }
    let mounted = true
    supabase
      .from('restaurants')
      .select('id')
      .eq('active', true)
      .order('created_at')
      .limit(1)
      .single()
      .then(({ data, error: e }) => {
        if (!mounted) return
        if (e) {
          // eslint-disable-next-line no-console
          console.warn('[useScheduleSuggestions] restaurant lookup failed', e)
          return
        }
        if (data?.id) setRid(data.id)
      })
    return () => { mounted = false }
  }, [currentRestaurantId])

  // Cache lookup on mount (and when restaurant_id resolves). Cache HIT
  // within 6h → 'cached' or 'empty'. Cache MISS → 'idle' (no auto-gen).
  useEffect(() => {
    if (!restaurantId) return
    let cancelled = false
    // Reset to idle so a previously-selected unit's suggestion doesn't
    // linger after a unit switch (cache MISS leaves state otherwise).
    setPhase('idle')
    setPayload(null)
    setGeneratedAt(null)
    setError(null)
    const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
    supabase
      .from('ai_briefs')
      .select('generated_at, payload')
      .eq('restaurant_id', restaurantId)
      .eq('tab_key', TAB_KEY)
      .eq('sub_key', SUB_KEY)
      .gte('generated_at', cutoff)
      .order('generated_at', { ascending: false })
      .limit(1)
      .then(({ data, error: e }) => {
        if (cancelled) return
        if (e) {
          // eslint-disable-next-line no-console
          console.warn('[useScheduleSuggestions] cache read failed', e)
          return
        }
        const row = (data && data[0]) || null
        if (!row) return
        setPayload(row.payload || null)
        setGeneratedAt(row.generated_at)
        if (row.payload && row.payload._empty) setPhase('empty')
        else setPhase('cached')
      })
    return () => { cancelled = true }
  }, [restaurantId])

  // Cooldown tick — single setInterval that runs while a cooldown is
  // active, then self-cleans. Reading from cooldownEndsAtRef means a
  // re-render won't restart the clock.
  const startCooldown = useCallback(() => {
    cooldownEndsAtRef.current = Date.now() + COOLDOWN_MS
    setCool(COOLDOWN_MS)
    if (cooldownTickRef.current) clearInterval(cooldownTickRef.current)
    cooldownTickRef.current = setInterval(() => {
      const remaining = Math.max(0, cooldownEndsAtRef.current - Date.now())
      setCool(remaining)
      if (remaining <= 0 && cooldownTickRef.current) {
        clearInterval(cooldownTickRef.current)
        cooldownTickRef.current = null
      }
    }, 1000)
  }, [])

  // Clear cooldown interval on unmount.
  useEffect(() => {
    return () => {
      if (cooldownTickRef.current) {
        clearInterval(cooldownTickRef.current)
        cooldownTickRef.current = null
      }
    }
  }, [])

  // generate(force) — POST to the edge function. Empty body so the
  // edge fn builds the snapshot server-side (with RLS-scoped JWT). The
  // `force` flag is kept for symmetry with useHeroBrief.regenerate but
  // we don't actually have a separate "cached" code path here — every
  // user-initiated call goes through the edge fn.
  const generate = useCallback(async (_force = false) => {
    if (!restaurantId) return
    if (inFlightRef.current) return
    if (cooldownRemainingMs > 0) return

    inFlightRef.current = true
    setError(null)
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/${FN_NAME}`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        // Empty body — let the edge fn build the snapshot server-side.
        // (Client-built snapshots are easier to manipulate; for a
        // higher-value AI call we lean on RLS-scoped REST reads.)
        // Grouped: send the selected restaurant_id so _shared/auth.ts
        // re-targets (verified against memberships, never trusted).
        body: JSON.stringify(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        setError(data.message || "We couldn't pull suggestions right now.")
        return
      }
      setPayload(data || null)
      setGeneratedAt(new Date().toISOString())
      if (data?._empty) setPhase('empty')
      else setPhase('ready')
      startCooldown()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useScheduleSuggestions] edge fn call failed', e)
      // Deterministic calm fallback. Never the raw error per CLAUDE.md.
      setPhase('error')
      setError("We couldn't pull suggestions right now.")
    } finally {
      inFlightRef.current = false
    }
  }, [restaurantId, cooldownRemainingMs, startCooldown, currentRestaurantId])

  return useMemo(
    () => ({
      phase,
      payload,
      generatedAt,
      cooldownRemainingMs,
      generate,
      error,
    }),
    [phase, payload, generatedAt, cooldownRemainingMs, generate, error],
  )
}
